import React from "react";
import { Link } from "react-router-dom";

import "./deviceGroups.css";

const aggregateStats = (groups = []) => {
  return groups.reduce(
    (accumulator, group) => {
      const totalDevices = Array.isArray(group.items)
        ? group.items.length
        : Number(group.itemsCount) || 0;
      const onlineDevices = Number(group.itemsOnline) || 0;
      const assignedUsers = Array.isArray(group.users)
        ? group.users.length
        : 0;

      return {
        groupCount: accumulator.groupCount + 1,
        totalDevices: accumulator.totalDevices + totalDevices,
        onlineDevices: accumulator.onlineDevices + onlineDevices,
        offlineDevices:
          accumulator.offlineDevices + Math.max(totalDevices - onlineDevices, 0),
        assignedUsers: accumulator.assignedUsers + assignedUsers,
      };
    },
    {
      groupCount: 0,
      totalDevices: 0,
      onlineDevices: 0,
      offlineDevices: 0,
      assignedUsers: 0,
    }
  );
};

const formatNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString();
  }

  return value;
};

export default class DeviceGroups extends React.Component {
  componentDidMount() {
    this.maybeFetchGroups();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.resources.groupsLoaded !== this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoaded &&
      !this.props.resources.groupsLoading
    ) {
      this.maybeFetchGroups();
    }
  }

  maybeFetchGroups = () => {
    const { resources = {}, fetchResource } = this.props;
    if (!resources.groupsLoaded && !resources.groupsLoading) {
      fetchResource("groups");
    }
  };

  state = {
    searchBar: "",
    currentFilter: "",
  };

  applyFilters = () => {
    this.setState({
      currentFilter: this.state.searchBar,
    });
  };

  clearFilters = () => {
    this.setState({
      searchBar: "",
      currentFilter: "",
    });
  };

  handleSearchChange = (event) => {
    this.setState({ searchBar: event.target.value });
  };

  handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      this.applyFilters();
    }
  };

  render() {
    const { resources = {} } = this.props;
    const { groupsLoading } = resources;

    const groups = (resources.groups || []).filter(
      (group) => group.groupType === "devices"
    );

    const filteredGroups = groups.filter((group) => {
      const name = (group.name || "").toLowerCase();
      const description = (group.description || "").toLowerCase();
      return `${name}${description}`.includes(
        this.state.currentFilter.toLowerCase()
      );
    });

    const isAdmin =
      this.props.user.has_admin_privileges ||
      this.props.user.is_enterprise_admin;

    const overallStats = aggregateStats(groups);
    const filteredStats = aggregateStats(filteredGroups);

    const header = (
      <div className="device-groups-header">
        <div>
          <h2 className="device-groups-header__title">Manage device groups</h2>
          <div className="device-groups-header__description">
            Organize PDUs into groups, assign access to the right teammates, and
            monitor fleet connectivity from one streamlined workspace.
          </div>
        </div>
        {isAdmin && (
          <div className="device-groups-header__actions">
            <Link to="/devices/groups/new" className="button">
              Create new group
            </Link>
          </div>
        )}
      </div>
    );

    if (groupsLoading) {
      return (
        <div className="device-groups-page">
          {header}
          <div className="device-groups-summary">
            {[1, 2, 3, 4].map((card) => (
              <div
                key={card}
                className="device-groups-summary-card device-groups-skeleton-card"
              >
                <div className="skeleton-blank" style={{ width: "40%" }} />
                <div
                  className="skeleton-blank"
                  style={{ width: "70%", height: "24px" }}
                />
                <div className="skeleton-blank" style={{ width: "55%" }} />
                <div className="device-groups-summary-card__accent" />
              </div>
            ))}
          </div>
          <div className="device-groups-table-wrapper">
            <div className="device-groups-table">
              {[1, 2, 3].map((row) => (
                <div
                  key={row}
                  className="device-groups-skeleton-row"
                  style={{ borderTop: row === 1 ? "none" : undefined }}
                >
                  {[1, 2, 3, 4, 5].map((cell) => (
                    <div key={cell}>
                      <div
                        className="skeleton-blank"
                        style={{ width: cell === 1 ? "80%" : "60%" }}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (groups.length === 0) {
      const emptyDescription = isAdmin
        ? "Device groups allow you to organize PDUs, assign users, and control permissions at scale. Create your first group to start managing devices together."
        : "Device groups help your organization organize PDUs and control access. Check back soon or contact an administrator for access.";

      return (
        <div className="device-groups-page">
          {header}
          <div className="device-groups-empty">
            <i className="material-icons device-groups-empty__icon">group_add</i>
            <div className="device-groups-empty__title">No device groups yet</div>
            <div className="device-groups-empty__description">
              {emptyDescription}
            </div>
            {isAdmin && (
              <div className="device-groups-empty__actions">
                <Link to="/devices/groups/new" className="button">
                  Create new group
                </Link>
              </div>
            )}
          </div>
        </div>
      );
    }

    const { searchBar } = this.state;

    const summaryCards = [
      {
        key: "groups",
        label: "Groups",
        value: formatNumber(filteredStats.groupCount),
        meta:
          overallStats.groupCount > 0
            ? `of ${formatNumber(overallStats.groupCount)} total`
            : "Filtered selection",
        accentColor: "#95a5a6",
      },
      {
        key: "online",
        label: "Online devices",
        value: formatNumber(filteredStats.onlineDevices),
        meta: filteredStats.totalDevices
          ? `out of ${formatNumber(filteredStats.totalDevices)} selected`
          : "No devices assigned yet",
        accentColor: "#2ecc71",
      },
      {
        key: "offline",
        label: "Offline devices",
        value: formatNumber(filteredStats.offlineDevices),
        meta: filteredStats.totalDevices
          ? `out of ${formatNumber(filteredStats.totalDevices)} selected`
          : "No devices in filter",
        accentColor: "#e74c3c",
      },
      {
        key: "members",
        label: "Assigned members",
        value: formatNumber(filteredStats.assignedUsers),
        meta:
          filteredStats.assignedUsers === 1
            ? "Team member across filtered groups"
            : "Team members across filtered groups",
        accentColor: "var(--quinary-color)",
      },
    ];

    return (
      <div className="device-groups-page">
        {header}

        <div className="device-groups-summary">
          {summaryCards.map((card) => (
            <div
              key={card.key}
              className="device-groups-summary-card"
              style={{ "--accent-color": card.accentColor }}
            >
              <div className="device-groups-summary-card__value">{card.value}</div>
              <div className="device-groups-summary-card__label">{card.label}</div>
              <div className="device-groups-summary-card__meta">{card.meta}</div>
              <div className="device-groups-summary-card__accent" />
            </div>
          ))}
        </div>

        <div className="device-groups-toolbar">
          <div className="device-groups-search">
            <input
              type="text"
              className="device-groups-search__input"
              placeholder="Search by group name or description"
              value={searchBar}
              onChange={this.handleSearchChange}
              onKeyDown={this.handleSearchKeyDown}
            />
            <button
              type="button"
              className="search-icon-button device-groups-search__button"
              onClick={this.applyFilters}
              aria-label="Search device groups"
            >
              <i className="material-icons">search</i>
            </button>
          </div>
          <div className="device-groups-toolbar__meta">
            {filteredStats.groupCount === 0
              ? "No groups match your filters"
              : `Showing ${formatNumber(filteredStats.groupCount)} of ${formatNumber(
                  overallStats.groupCount
                )} groups`}
          </div>
        </div>

        {filteredGroups.length === 0 ? (
          <div className="device-groups-empty">
            <i className="material-icons device-groups-empty__icon">filter_alt</i>
            <div className="device-groups-empty__title">
              No groups match your search
            </div>
            <div className="device-groups-empty__description">
              Try adjusting your keywords or clearing the filters to see all
              available device groups.
            </div>
            <div className="device-groups-empty__actions">
              <button
                type="button"
                className="device-groups-table__manage"
                onClick={this.clearFilters}
              >
                Clear search
              </button>
            </div>
          </div>
        ) : (
          <div className="device-groups-table-wrapper">
            <div className="device-groups-table">
              <div className="device-groups-table__head">
                <div>Group</div>
                <div>Devices</div>
                <div>Status</div>
                <div>Users</div>
                <div style={{ textAlign: "right" }}>Actions</div>
              </div>
              {filteredGroups.map((groupObj) => {
                const totalDevices = Array.isArray(groupObj.items)
                  ? groupObj.items.length
                  : Number(groupObj.itemsCount) || 0;
                const onlineDevices = Number(groupObj.itemsOnline) || 0;
                const offlineDevices = Math.max(totalDevices - onlineDevices, 0);
                const onlinePercent = totalDevices
                  ? Math.round((onlineDevices / totalDevices) * 100)
                  : 0;
                const description = groupObj.description?.trim()
                  ? groupObj.description
                  : "No description provided";
                const userCount = Array.isArray(groupObj.users)
                  ? groupObj.users.length
                  : 0;

                return (
                  <div className="device-groups-table__row" key={groupObj.id}>
                    <div className="device-groups-table__name">
                      <Link to={`/devices/groups/${groupObj.id}`}>
                        {groupObj.name}
                      </Link>
                      <div className="device-groups-table__description">
                        {description}
                      </div>
                    </div>
                    <div className="device-groups-table__metric">
                      <strong>{formatNumber(totalDevices)}</strong>
                      <span>Total devices</span>
                      <div className="device-groups-table__bar">
                        <div
                          className="device-groups-table__bar-fill"
                          style={{ width: `${onlinePercent}%` }}
                        />
                      </div>
                    </div>
                    <div className="device-groups-table__metric">
                      <div className="device-groups-table__status-online">
                        {formatNumber(onlineDevices)} online
                      </div>
                      <div className="device-groups-table__status-offline">
                        {formatNumber(offlineDevices)} offline
                      </div>
                    </div>
                    <div className="device-groups-table__users">
                      <strong>{formatNumber(userCount)}</strong>
                      <span>
                        {userCount === 1 ? "Assigned member" : "Assigned members"}
                      </span>
                    </div>
                    <div className="device-groups-table__actions">
                      <Link
                        to={`/devices/groups/${groupObj.id}`}
                        className="device-groups-table__manage"
                      >
                        View group
                      </Link>
                      {isAdmin && (
                        <Link
                          to={`/devices/groups/${groupObj.id}/settings`}
                          className="device-groups-table__manage"
                        >
                          Manage
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
}
